export const SDK_NAME = '@biqli/analytics';
export const SDK_VERSION = '2.0.1';
export const DEFAULT_API_HOST = 'https://biq.li';

export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

export function createId(prefix = 'evt'): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi?.randomUUID) return `${prefix}_${cryptoApi.randomUUID()}`;
  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    const random = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${prefix}_${random}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
}

export function normalizeApiHost(value?: string): string {
  const input = (value || DEFAULT_API_HOST).trim();
  if (!input) return DEFAULT_API_HOST;
  const url = new URL(input, isBrowser() ? window.location.origin : DEFAULT_API_HOST);
  if (
    !['http:', 'https:'].includes(url.protocol) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash
  ) {
    throw new TypeError(
      'The Biqli API host must use HTTP or HTTPS without credentials, query, or hash.',
    );
  }
  return url.toString().replace(/\/$/, '');
}

export function endpointUrl(apiHost: string, path: string): string {
  return `${apiHost.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

export function compactObject<T extends Record<string, unknown>>(value: T): T {
  const compacted: Record<string, unknown> = {};
  Object.keys(value).forEach(key => {
    if (value[key] !== undefined) compacted[key] = value[key];
  });
  return compacted as T;
}

export function keyFingerprint(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function normalizeStringList(values: string[] | undefined, fallback: string[] = []): string[] {
  const result = (values ?? fallback).map(value => value.trim()).filter(Boolean);
  return Array.from(new Set(result));
}

export function isValidClickId(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(value);
}

export function toUtcTimestamp(value?: string): string | undefined {
  return value || undefined;
}
